import React, { useState, useEffect } from 'react';
import '../App.css';
import Header from '../components/Header';
import TournamentBracket from '../components/TournamentBracket';
import PrizesSection from '../components/PrizesSection';
import PlayerModal from '../components/PlayerModal';
import MatchModal from '../components/MatchModal';
import Login from '../components/Login';
import Footer from '../components/Footer';
import { updateTournamentBracket } from '../utils/bracketGenerator';
import { API_BASE } from '../config/api';

const AdminView = () => {
  const [tournamentData, setTournamentData] = useState({
    roundOf16: [],
    groupOf8: [],
    quarterFinals: [],
    semiFinals: [],
    final: null
  });
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [selectedRound, setSelectedRound] = useState(null);
  const [selectedPlayerNumber, setSelectedPlayerNumber] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);

  useEffect(() => {
    checkAuthStatus();
    fetchTournamentData();
    
    // Fetch tournament data periodically (less frequent for admin since they update optimistically)
    const interval = setInterval(() => {
      fetchTournamentData();
    }, 3000); // Update every 3 seconds for consistency check

    return () => clearInterval(interval);
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await fetch(`${API_BASE}/auth/status`, {
        credentials: 'include'
      });
      const data = await response.json();
      setIsAuthenticated(data.isAuthenticated);
      if (!data.isAuthenticated) {
        setShowLogin(true);
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTournamentData = async () => {
    try {
      const response = await fetch(`${API_BASE}/tournament`, {
        credentials: 'include'
      });
      const data = await response.json();
      setTournamentData(data);
    } catch (error) {
      console.error('Error fetching tournament data:', error);
    }
  };

  const handleLogin = () => {
    setIsAuthenticated(true);
    setShowLogin(false);
    fetchTournamentData();
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE}/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      setIsAuthenticated(false);
      setShowLogin(true);
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handlePlayerClick = (player, match, round, playerNumber) => {
    if (player && player.name && !player.isPlaceholder) {
      // Close match modal when opening player modal
      setSelectedMatch(match || null);
      setSelectedRound(round || null);
      setSelectedPlayerNumber(playerNumber || null);
      setSelectedPlayer(player);
    }
  };

  const handleMatchClick = (match, round) => {
    if (match) {
      setSelectedMatch(match);
      setSelectedRound(round);
      setSelectedPlayer(null);
    }
  };

  const closePlayerModal = () => {
    setSelectedPlayer(null);
    setSelectedMatch(null);
    setSelectedRound(null);
    setSelectedPlayerNumber(null);
  };

  const closeMatchModal = () => {
    setSelectedMatch(null);
    setSelectedRound(null);
  };

  const handleWinnerUpdate = async (match, round, winner) => {
    if (!isAuthenticated) return;

    // Validate round - use selectedRound as fallback if round is not provided
    const validRound = round || selectedRound;
    if (!validRound) {
      console.error('Round is missing in handleWinnerUpdate:', { match, round, selectedRound, winner });
      alert('Error: Round information is missing. Please close and reopen the match.');
      return;
    }

    // Use validRound for the rest of the function
    const roundToUse = validRound;

    // Optimistic update - update UI immediately
    setTournamentData(prevData => {
      const updated = { ...prevData };
      
      if (roundToUse === 'roundOf16') {
        const matchIndex = updated.roundOf16.findIndex(m => m.id === match.id);
        if (matchIndex !== -1) {
          updated.roundOf16[matchIndex] = {
            ...updated.roundOf16[matchIndex],
            winner
          };
        }
      } else if (roundToUse === 'groupOf8') {
        const matchIndex = updated.groupOf8.findIndex(m => m.id === match.id);
        if (matchIndex !== -1) {
          updated.groupOf8[matchIndex] = {
            ...updated.groupOf8[matchIndex],
            winner
          };
        }
      } else if (roundToUse === 'quarterFinals') {
        const matchIndex = updated.quarterFinals.findIndex(m => m.id === match.id);
        if (matchIndex !== -1) {
          updated.quarterFinals[matchIndex] = {
            ...updated.quarterFinals[matchIndex],
            winner
          };
        }
      } else if (roundToUse === 'semiFinals') {
        const matchIndex = updated.semiFinals.findIndex(m => m.id === match.id);
        if (matchIndex !== -1) {
          updated.semiFinals[matchIndex] = {
            ...updated.semiFinals[matchIndex],
            winner
          };
        }
      } else if (roundToUse === 'final') {
        if (updated.final && updated.final.id === match.id) {
          updated.final = {
            ...updated.final,
            winner
          };
        }
      }

      // Update bracket with potential matchups
      return updateTournamentBracket(updated);
    });

    // Update selected match in modal immediately
    if (selectedMatch && selectedMatch.id === match.id) {
      setSelectedMatch(prevMatch => ({
        ...prevMatch,
        winner
      }));
    }

    // Sync with server in background
    try {
      console.log('Sending winner update to server:', { 
        round: roundToUse, 
        matchId: match.id, 
        match: match,
        winner, 
        originalRound: round, 
        selectedRound 
      });
      const response = await fetch(`${API_BASE}/tournament/match`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          round: roundToUse,
          matchId: match.id,
          updates: { winner }
        }),
      });

      if (response.ok) {
        // Refresh from server to ensure consistency
        await fetchTournamentData();
      } else {
        const error = await response.json();
        console.error('Error updating winner:', error);
      }
    } catch (error) {
      console.error('Error syncing winner with server:', error);
    }
  };

  const handleScoreUpdate = async (match, round, updates) => {
    if (!isAuthenticated) return;

    // Validate round - use selectedRound as fallback if round is not provided
    const validRound = round || selectedRound;
    if (!validRound) {
      console.error('Round is missing in handleScoreUpdate:', { match, round, selectedRound, updates });
      alert('Error: Round information is missing. Please close and reopen the match.');
      return;
    }

    // Use validRound for the rest of the function
    const roundToUse = validRound;

    // Optimistic update - update UI immediately
    setTournamentData(prevData => {
      const updated = { ...prevData };
      
      if (roundToUse === 'roundOf16') {
        const matchIndex = updated.roundOf16.findIndex(m => m.id === match.id);
        if (matchIndex !== -1) {
          updated.roundOf16[matchIndex] = {
            ...updated.roundOf16[matchIndex],
            ...updates
          };
        }
      } else if (roundToUse === 'groupOf8') {
        const matchIndex = updated.groupOf8.findIndex(m => m.id === match.id);
        if (matchIndex !== -1) {
          updated.groupOf8[matchIndex] = {
            ...updated.groupOf8[matchIndex],
            ...updates
          };
        }
      } else if (roundToUse === 'quarterFinals') {
        const matchIndex = updated.quarterFinals.findIndex(m => m.id === match.id);
        if (matchIndex !== -1) {
          updated.quarterFinals[matchIndex] = {
            ...updated.quarterFinals[matchIndex],
            ...updates
          };
        }
      } else if (roundToUse === 'semiFinals') {
        const matchIndex = updated.semiFinals.findIndex(m => m.id === match.id);
        if (matchIndex !== -1) {
          updated.semiFinals[matchIndex] = {
            ...updated.semiFinals[matchIndex],
            ...updates
          };
        }
      } else if (roundToUse === 'final') {
        if (updated.final && updated.final.id === match.id) {
          updated.final = {
            ...updated.final,
            ...updates
          };
        }
      }

      // Update bracket with potential matchups
      return updateTournamentBracket(updated);
    });

    // Update selected match in modal immediately
    if (selectedMatch && selectedMatch.id === match.id) {
      setSelectedMatch(prevMatch => ({
        ...prevMatch,
        ...updates
      }));
    }

    // Sync with server in background
    try {
      console.log('Sending score update to server:', { round: roundToUse, matchId: match.id, updates, originalRound: round, selectedRound });
      const response = await fetch(`${API_BASE}/tournament/match`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          round: roundToUse,
          matchId: match.id,
          updates
        }),
      });

      if (response.ok) {
        // Refresh from server to ensure consistency
        await fetchTournamentData();
      } else {
        const error = await response.json();
        console.error('Error updating scores:', error);
        // Optionally show a non-intrusive error message
      }
    } catch (error) {
      console.error('Error syncing scores with server:', error);
      // Optionally show a non-intrusive error message
    }
  };

  if (isLoading) {
    return (
      <div className="App">
        <div className="loading-screen">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <Header isAuthenticated={isAuthenticated} onLogout={handleLogout} isAdminPage={true} />
      {showLogin && !isAuthenticated && (
        <Login onLogin={handleLogin} />
      )}
      <main className="main-content">
        {isAuthenticated && (
          <div className="admin-banner">
            <span>🔒 Admin Mode - You can edit matches and scores</span>
          </div>
        )}
        <TournamentBracket 
          tournamentData={tournamentData}
          onPlayerClick={handlePlayerClick}
          onMatchClick={handleMatchClick}
        />
        <PrizesSection />
      </main>
      {selectedPlayer && (
        <PlayerModal 
          player={selectedPlayer} 
          onClose={closePlayerModal}
          isAdmin={isAuthenticated}
          round={selectedRound}
          matchId={selectedMatch?.id}
          playerNumber={selectedPlayerNumber}
          onUploadSuccess={fetchTournamentData}
        />
      )}
      {selectedMatch && selectedRound && !selectedPlayer && (
        <MatchModal 
          match={selectedMatch} 
          round={selectedRound}
          onClose={closeMatchModal}
          onPlayerClick={handlePlayerClick}
          onWinnerUpdate={handleWinnerUpdate}
          isAdmin={isAuthenticated}
          onScoreUpdate={handleScoreUpdate}
        />
      )}
      <Footer />
    </div>
  );
};

export default AdminView;
